import React, { useState, useRef } from "react";
import SEO from "../components/SEO";

export default function Contact() {
  const formRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Client-side file validation constraints
  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 10;
  const MAX_TOTAL_SIZE_MB = 20;
  const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.value ? e.target.files : []);
    if (files.length === 0) return;

    // Validate file count
    if (files.length > MAX_FILES) {
      alert(`Du kan højst uploade ${MAX_FILES} filer.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    // Validate individual file size and type
    let totalSize = 0;
    for (let file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Filtypen for "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP er tilladt.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        alert(`Filen "${file.name}" er for stor. Hver fil må højst fylde ${MAX_FILE_SIZE_MB} MB.`);
        e.target.value = "";
        setSelectedFiles([]);
        return;
      }
      totalSize += file.size;
    }

    // Validate total size
    if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
      alert(`Den samlede størrelse af filerne overstiger grænsen på ${MAX_TOTAL_SIZE_MB} MB.`);
      e.target.value = "";
      setSelectedFiles([]);
      return;
    }

    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    const form = formRef.current;
    const formData = new FormData(form);

    // Verify honeypot field is empty
    const honeypot = formData.get("website_url");
    if (honeypot) {
      // Silent fail for bots
      setIsSubmitting(false);
      setSubmitStatus("success");
      form.reset();
      setSelectedFiles([]);
      return;
    }

    try {
      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === "ok") {
        setSubmitStatus("success");
        form.reset();
        setSelectedFiles([]);
      } else {
        setSubmitStatus("error");
        setErrorMessage(result.message || "Der opstod en fejl under afsendelsen. Prøv venligst igen.");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitStatus("error");
      setErrorMessage("Netværksfejl. Kontroller din internetforbindelse og prøv igen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-[72px] bg-brand-sand-50 min-h-screen">
      <SEO
        title="Kontakt | Elmegaarden Have og Anlæg"
        description="Du kan i nedenstående formular sende os en direkte besked. Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag."
        ogImage="https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/elmegaarden-0007.jpg"
        canonical="/kontakt"
      />

      {/* Contact Section */}
      <section className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Contact Info (Left Column) */}
          <div className="lg:col-span-5 flex flex-col gap-10 text-left">
            <div className="flex flex-col gap-4">
              <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-green-900 leading-tight">
                Kontakt
              </h1>
              <p className="text-brand-charcoal/80 text-base leading-relaxed">
                Du kan i nedenstående formular sende os en direkte besked.
              </p>
              <p className="text-brand-charcoal/80 text-sm md:text-base leading-relaxed">
                Vi bestræber os altid på at svare alle henvendelser hurtigst muligt - men giv os endeligt et kald hvis du har brug for et svar idag.
              </p>
            </div>

            {/* Contact Details List */}
            <div className="flex flex-col gap-6 bg-white p-8 rounded-sm border border-brand-sand-200/80 shadow-xs">
              <div className="flex flex-col gap-1">
                <span className="text-xs font-bold text-[#7ca632] uppercase tracking-wider">Virksomhed</span>
                <span className="font-bold text-lg text-brand-green-950">Elmegaarden Have og Anlæg</span>
                <span className="text-sm text-brand-charcoal/80">Stenstrupvej 5, 9500 Hobro</span>
              </div>

              <div className="pt-4 border-t border-brand-sand-100 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-phone text-[#025e40] text-base shrink-0 w-5 text-center" />
                  <span className="text-sm text-brand-charcoal/70">Tlf:</span>
                  <a href="tel:+4520661517" className="text-brand-green-900 font-bold hover:text-[#7ca632] transition-colors">
                    20 66 15 17
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-envelope text-[#025e40] text-base shrink-0 w-5 text-center" />
                  <span className="text-sm text-brand-charcoal/70">E-mail:</span>
                  <a href="mailto:rasmus@elmegaardenhaveoganlaeg.dk" className="text-brand-green-900 font-bold hover:text-[#7ca632] transition-colors break-all">
                    rasmus@elmegaardenhaveoganlaeg.dk
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <i className="fa-solid fa-id-card text-[#025e40] text-base shrink-0 w-5 text-center" />
                  <span className="text-sm text-brand-charcoal/70">CVR:</span>
                  <span className="text-brand-green-900 font-bold">42349127</span>
                </div>
              </div>

              <div className="pt-4 border-t border-brand-sand-100">
                <a
                  href="https://www.facebook.com/ElmegaardenHaveogAnlaeg"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1877f2] hover:bg-[#166fe5] text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors"
                >
                  <i className="fa-brands fa-facebook-f" /> Følg os på Facebook
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form (Right Column) */}
          <div className="lg:col-span-7 bg-white p-8 md:p-12 rounded-sm border border-brand-sand-200/80 shadow-xs text-left">
            <h2 className="font-display text-2xl font-bold tracking-tight text-brand-green-900 mb-6">
              Kontakt os
            </h2>
            <p className="text-sm text-brand-charcoal/70 mb-8">
              Du kan let og hurtigt sende os en besked i nedenstående formular.
            </p>

            {submitStatus === "success" && (
              <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm flex items-start gap-4">
                <i className="fa-solid fa-circle-check text-emerald-600 text-xl shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Tak for din besked!</h4>
                  <p className="text-sm text-emerald-800 mt-1">
                    Vi har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-8 p-6 bg-rose-50 border border-rose-200 text-rose-900 rounded-sm flex items-start gap-4">
                <i className="fa-solid fa-circle-exclamation text-rose-600 text-xl shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-base">Der opstod en fejl</h4>
                  <p className="text-sm text-rose-800 mt-1">{errorMessage}</p>
                </div>
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Secure Antispam Honeypot Field */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke denne)</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {/* Name Field */}
              <div className="flex flex-col gap-2 text-left">
                <label htmlFor="name" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                  Navn <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="Dit fulde navn"
                  className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                />
              </div>

              {/* Phone Field */}
              <div className="flex flex-col gap-2 text-left">
                <label htmlFor="phone" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                  Telefon <span className="text-rose-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  placeholder="Dit telefonnummer (fx 20 66 15 17)"
                  className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                />
              </div>

              {/* Email Field */}
              <div className="flex flex-col gap-2 text-left">
                <label htmlFor="email" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                  E-mail <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="din@email.dk"
                  className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors"
                />
              </div>

              {/* Description Field */}
              <div className="flex flex-col gap-2 text-left">
                <label htmlFor="description" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                  Besked <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="5"
                  placeholder="Skriv din besked eller forespørgsel her..."
                  className="w-full px-4 py-3 bg-brand-sand-50 border border-brand-sand-300 rounded-sm focus:outline-none focus:border-brand-green-700 text-sm transition-colors resize-none"
                />
              </div>

              {/* File Upload Field */}
              <div className="flex flex-col gap-2 text-left">
                <label htmlFor="files" className="text-xs font-bold tracking-wider uppercase text-brand-green-900">
                  Vedhæft filer (valgfrit)
                </label>
                <div className="border-2 border-dashed border-brand-sand-300 rounded-sm p-6 bg-brand-sand-50 text-center hover:border-brand-green-700 transition-colors relative">
                  <input
                    type="file"
                    id="files"
                    name="files"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={handleFileChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center gap-2">
                    <i className="fa-light fa-cloud-arrow-up text-2xl text-brand-sand-600" />
                    <span className="text-sm font-medium text-brand-green-900">Klik eller træk filer hertil</span>
                    <span className="text-xs text-brand-sand-600">
                      Tilladte formater: PDF, PNG, JPG, GIF, WEBP (Maks. 5 filer, 10 MB pr. fil, 20 MB i alt)
                    </span>
                  </div>
                </div>

                {/* Selected Files Preview */}
                {selectedFiles.length > 0 && (
                  <div className="mt-3 flex flex-col gap-2">
                    <span className="text-xs font-bold text-brand-green-900 uppercase tracking-wider">Valgte filer:</span>
                    <ul className="text-xs text-brand-charcoal/80 space-y-1">
                      {selectedFiles.map((file, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <i className="fa-light fa-file text-brand-sand-600" />
                          <span className="font-medium">{file.name}</span>
                          <span className="text-brand-sand-600">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-sm tracking-wide uppercase rounded-sm transition-all duration-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="fa-light fa-spinner-third animate-spin" />
                    Sender besked...
                  </span>
                ) : (
                  "Send besked"
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
